import { useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { MoreVertIcon } from "icons";

import ButtonDropdown from "components/ButtonDropdown";
import GroupLabel from "components/ButtonDropdown/GroupLabel";
import { MenuItem } from "components/Menu";
import Stack from "components/Stack";
import Text from "components/Text";

import { useLoggedUserIsDealAdmin } from "entities/Deal/hooks";
import { useDealChecklistPermission } from "entities/Deal/sdk";
import ShareWithButton from "entities/Package/components/ShareWithButton";
import StatusSelector from "entities/Package/components/StatusSelector";
import { IPackageSection } from "entities/Package/sdk";
import DueDiligenceSectionNewTaskButton from "entities/Task/components/DueDiligenceSectionNewTaskButton";
import DueDiligenceSectionOpenTaskButton from "entities/Task/components/DueDiligenceSectionOpenTaskButton";
import { useTaskChannels } from "entities/Task/sdk";

interface IPackageSectionMenuButton {
  section: IPackageSection;
  dealId: number;
  categoryId: number;
  updatePackage: ({ message }: { message?: string }) => void;
  handleAllMarksAsSeen: () => void;
  menuButtonDataTestid?: string;
}

const PackageSectionMenuButton = ({
  section,
  dealId,
  categoryId,
  updatePackage,
  handleAllMarksAsSeen,
  menuButtonDataTestid,
}: IPackageSectionMenuButton) => {
  const params = useParams<{
    packageId: string;
    tabId: string;
    fieldId: string;
  }>();

  const tabId = Number(params.tabId);
  const packageId = Number(params.packageId);
  const { loggedUserIsDealAdmin } = useLoggedUserIsDealAdmin({ dealId });

  const { data: userPermission } = useDealChecklistPermission({
    dealId,
    packageId,
  });
  const { data: taskChannels } = useTaskChannels({ dealId });
  const tasksForSection = useMemo(
    () =>
      taskChannels?.filter(
        (taskChannel) =>
          taskChannel.task.company_category?.id === categoryId &&
          taskChannel.task.company_section?.id === section.section_id &&
          taskChannel.task.company_field === null &&
          !taskChannel.task.completed
      ) || [],
    [categoryId, section, taskChannels]
  );

  const hasFullAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.is_full_access,
    [userPermission]
  );

  const hasWriteAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.has_write_access,
    [userPermission]
  );

  const fieldIds = useMemo(
    () => section.fields.map((field) => field.field_id),
    [section]
  );

  return (
    <Stack flexDirection="row">
      {tasksForSection.length > 0 && (
        <DueDiligenceSectionOpenTaskButton
          taskChannels={tasksForSection}
          section={section}
          dealId={dealId}
          tabId={tabId}
          categoryId={categoryId}
          packageId={packageId}
          updatePackage={updatePackage}
          taskButtonDataTestid="dd-open-task-button"
          taskMessagesButtonDataTestid="dd-open-new-task-messages-button"
        />
      )}
      <ButtonDropdown
        variant="text"
        label={<MoreVertIcon />}
        width="auto"
        size="small"
        sx={{ padding: "2px" }}
        keepMenuMounted
        buttonDataTestid={menuButtonDataTestid}
      >
        {hasWriteAccess && <GroupLabel label="status: mark all as" />}
        {hasWriteAccess && (
          <StatusSelector
            fieldIds={fieldIds}
            companyPackageId={packageId}
            dealId={dealId}
            successCallback={updatePackage}
          />
        )}

        <GroupLabel label="actions" />

        {loggedUserIsDealAdmin && (
          <ShareWithButton initiallySelectedItemIds={[section.section_id]} />
        )}

        <MenuItem
          onClick={handleAllMarksAsSeen}
          dataTestid="clear-updated-tags-option"
        >
          <Text variant="text2" data-testid="action-menu-option-name">
            Clear updated tags
          </Text>
        </MenuItem>

        {hasFullAccess && (
          <DueDiligenceSectionNewTaskButton
            section={section}
            packageId={packageId}
            tabId={tabId}
            categoryId={categoryId}
            updatePackage={updatePackage}
            menuOptionDataTestid="create-new-task-option"
          />
        )}
      </ButtonDropdown>
    </Stack>
  );
};

export default PackageSectionMenuButton;
